<template>
<div>
  <v-container fluid class="my-5">
    <v-layout row wrap>
      <v-alert v-for="item in userQueueCreating" :key="item.id" text dense color="primary" icon="mdi-clock-fast" border="left" class="ml-2">{{ $t('views.adminUsers.creatingUser', { name: `${item.firstName} ${item.lastName} (${item.email})` }) }}</v-alert>
    </v-layout>
    <v-layout row justify-center>
      <v-data-table dense :headers="headers" :items="users" class="" style="width: 99%;">
        <template v-slot:top>
          <v-toolbar flat color="white">
            <v-toolbar-title>{{ $t('views.adminUsers.title') }}</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-dialog v-model="createDialog" fullscreen hide-overlay transition="dialog-bottom-transition" persistent>
              <template v-slot:activator="{ on: dialog }">
                <v-btn small color="#212121" class="white--text" v-on="{...dialog}">
                  <v-icon class="align-center" color="primary">mdi-plus-thick</v-icon>
                  <span class="pl-2">{{ $t('views.adminUsers.createUser') }}</span>
                </v-btn>
              </template>
              <create-user v-if="createDialog" @close-dialog="createDialog = false; editItem = null;" :created-by="authUser.uid" :existing-users="existingUsers" :editing-user="editItem" />
            </v-dialog>
            <v-dialog v-model="deleteDialog" max-width="400">
              <v-card>
                <v-card-title class="headline">{{ $t('views.adminUsers.warningTitle') }}</v-card-title>
                <v-card-text>
                  {{ $t('views.adminUsers.deleteConfirmation', { name: deleteItem.firstName + ' ' + deleteItem.lastName }) }}
                  <br/>
                  {{ $t('views.adminUsers.deleteWarning') }}
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="gray darken-1" text @click="deleteDialog = false">{{ $t('views.adminUsers.cancel') }}</v-btn>
                  <v-btn color="red darken-1" text @click="triggerDeleteUser(deleteItem.id); deleteDialog = false">{{ $t('views.adminUsers.delete') }}</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-toolbar>
        </template>
        <template v-slot:item.isAdmin="{ item }">
          {{ 'isAdmin' in item ? item.isAdmin.toString() : '' }}
        </template>
        <template v-slot:item.isManager="{ item }">
          {{ 'isManager' in item ? item.isManager.toString() : '' }}
        </template>
        <template v-slot:item.isScientist="{ item }">
          {{ 'isScientist' in item ? item.isScientist.toString(): '' }}
        </template>
        <template v-slot:item.isSupervisor="{ item }">
          {{ 'isSupervisor' in item ? item.isSupervisor.toString() : '' }}
        </template>
        <template v-slot:item.isCustomizer="{ item }">
          {{ 'isCustomizer' in item ? item.isCustomizer.toString() : '' }}
        </template>
        <template v-slot:item.date_created="{ item }">
          {{ formatDate(item) }}
        </template>
        <template v-slot:item.action="{ item }">
          <span v-if="!deleteQueue.includes(item.id)">
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn icon v-on="on" @click.stop="createDialog = true; editItem = item">
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
              </template>
              {{ $t('views.adminUsers.editUser') }}
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn icon v-on="on" @click.stop="deleteDialog = true; deleteItem = item">
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </template>
              {{ $t('views.adminUsers.deleteUser') }}
            </v-tooltip>
          </span>
          <v-chip class="ma-2" color="red" text-color="white" v-else>{{ $t('views.adminUsers.deleting') }}</v-chip>
        </template>
      </v-data-table>
    </v-layout>
  </v-container>
</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import CreateUser from '@/components/CreateUser.vue'

export default {
  data () {
    return {
      createDialog: false,
      deleteDialog: false,
      deleteItem: '',
      editItem: null,
      deleteQueue: []
    }
  },
  methods: {
    ...mapActions('admin', [
      'loadUsers',
      'loadUserQueue',
      'deleteUser'
    ]),
    sortByDate (a, b) {
      if (a && b) {
        const aDate = this.formatDate({
          date_created: a
        })
        const bDate = this.formatDate({
          date_created: b
        })
        return bDate.localeCompare(aDate)
      }
    },
    formatDate (item) {
      if (item && 'date_created' in item) {
        return new Date(Date.parse(item.date_created)).toISOString().slice(0, 10)
      }
    },
    triggerDeleteUser (id) {
      this.deleteQueue.push(id)
      this.deleteUser({
        uid: id
      })
    }
  },
  computed: {
    ...mapGetters('admin', [
      'users',
      'userQueue'
    ]),
    ...mapGetters('auth', [
      'authUser'
    ]),
    headers () {
      return [
        { text: this.$t('views.adminUsers.headerFirstName'), value: 'firstName' },
        { text: this.$t('views.adminUsers.headerLastName'), value: 'lastName' },
        { text: this.$t('views.adminUsers.headerEmail'), value: 'email' },
        { text: this.$t('views.adminUsers.headerAdmin'), value: 'isAdmin' },
        { text: this.$t('views.adminUsers.headerManager'), value: 'isManager' },
        { text: this.$t('views.adminUsers.headerScientist'), value: 'isScientist' },
        { text: this.$t('views.adminUsers.headerSupervisor'), value: 'isSupervisor' },
        { text: this.$t('views.adminUsers.headerCustomizer'), value: 'isCustomizer' },
        { text: this.$t('views.adminUsers.headerDomain'), value: 'domain' },
        { text: this.$t('views.adminUsers.headerCreated'), value: 'date_created', sort: this.sortByDate },
        { text: this.$t('views.adminUsers.headerActions'), value: 'action', sortable: false, width: 110 }
      ]
    },
    userQueueCreating: function () {
      var users = []
      for (var i in this.userQueue) {
        var item = this.userQueue[i]
        if (!('operation_successful' in item)) {
          if (item.operation === 'create') {
            users.push({
              ...item
            })
          }
        }
      }
      return users
    },
    existingUsers: function () {
      var users = []
      this.users.forEach(element => {
        users.push(element.email)
      })
      return users
    }
  },
  components: {
    CreateUser
  },
  created: function () {
    this.loadUsers()
    this.loadUserQueue()
  }
}
</script>
